import { useState, useEffect, useRef, createElement } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { donatePage as d, share, currencyUi } from '../data';
import { buildIgPayment, calcFeeCoverAmount, newPaymentToken } from '../igIframe';
import { logDonationEvent, fetchGramsciProject, submitDonationMessage } from '../api';
import { isPetition, CAMPAIGN } from '../projectType';
import { htmlToParagraphs, htmlToPlainText } from '../richText';
import { FIELD_RULES, validateForm as runValidation } from '../validation';
import { useAbandonedForm } from '../hooks/useAbandonedForm';
import { useIgFrameHeight } from '../hooks/useIgFrameHeight';
import { useBitRedirect } from '../hooks/useBitRedirect';
import { readUtmParams } from '../utmParams';
import { resolvePurchaseValue, trackPurchase } from '../analytics';
import ThankYouModal from '../components/ThankYouModal';
import CurrencyPicker from '../components/CurrencyPicker';
import ForeignAddressFields from '../components/ForeignAddressFields';
import { useConsentTermsDialog, TermsLink } from '../components/ConsentTerms';
import OtherCampaigns, { displayPct } from '../components/OtherCampaigns';
import {
  DEFAULT_CURRENCY, currencyMeta, formatAmount, presetAmounts,
  needsAddress, countryName,
} from '../currencies';

// WhatsApp CTA target (same number the share dialog uses).
const WHATSAPP_URL =
  'https://api.whatsapp.com/send/?phone=972552462608&text=%D7%94%D7%99%D7%99+%D9%85%D8%B1%D8%AD%D8%A8%D8%A7&type=phone_number&app_absent=0';

const emptyForm = {
  firstName: '', lastName: '', city: '', phone: '', email: '',
  // Foreign-currency donations only — see ForeignAddressFields.
  street: '', zipCode: '', countryId: '',
};

// Per-field donor validation — the shared rules from validation.js, same as the
// donate/join/petition pages use.
const VALIDATIONS = [
  FIELD_RULES.firstName, FIELD_RULES.lastName, FIELD_RULES.city,
  FIELD_RULES.phone, FIELD_RULES.email,
];

// Only asked when the donation is in a foreign currency, because that's the
// only time IsraelGives requires them (see ForeignAddressFields).
const FOREIGN_VALIDATIONS = [
  { key: 'street', pattern: /^.{2,}$/, message: currencyUi.errors.street.he },
  { key: 'zipCode', pattern: /^.{2,7}$/, message: currencyUi.errors.zipCode.he },
  { key: 'countryId', pattern: /^\d+$/, message: currencyUi.errors.country.he },
];

function validateForm(form, currency = DEFAULT_CURRENCY) {
  return runValidation(
    form,
    needsAddress(currency) ? [...VALIDATIONS, ...FOREIGN_VALIDATIONS] : VALIDATIONS
  );
}

// Live countdown to `endDate`, refreshed each minute. Returns null once passed.
function useCountdown(endDate) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  if (!endDate) return null;
  const diff = new Date(endDate).getTime() - now;
  if (!Number.isFinite(diff) || diff <= 0) return null;
  const minutes = Math.floor(diff / 60000);
  return { days: Math.floor(minutes / 1440), hours: Math.floor((minutes % 1440) / 60), minutes: minutes % 60 };
}

// Suggested amounts when the Gramsci record doesn't provide any.
const DEFAULT_ONCE_AMOUNTS = ['18', '50', '100', '250', '500', '1000', '2500'];
const DEFAULT_MONTHLY_AMOUNTS = ['18', '50', '100', '200', '400', '800', '1600'];

// Donor wall rows. Gramsci sends two lists: `donations_with_messages` (gifts
// whose donor wrote something) and `recent_donations` (the latest gifts, with
// or without one). The wall shows the ones carrying messages first, then the
// recent list behind them — a gift that appears in both is only shown once.
// Both lists were empty in every response seen so far, so each row is read
// tolerantly: only the keys we can confirm are relied on, and a nameless
// (anonymous) donor still gets a card.
const ANONYMOUS = 'אנונימי مجهول';

// Gramsci sends money as a numeric string with agorot ("500.00"). The wall
// shows whole shekels only, so the trailing ".00" never reaches the card.
// Rounded UP, as the campaign total is (see money() in normalizeCampaign) — a
// gift is never shown as smaller than it was.
const wholeShekels = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.ceil(n) : 0;
};

// A recurring gift earns a "תמיכה חודשית" line under the donor's name. Gramsci
// reports it as frequency 1; rows this app wrote went out with the string
// 'monthly' (server/gramsci.js buildBody), so both spellings count. Anything
// else — one-time, or the field missing entirely — leaves the line off.
const isMonthly = (f) => f === 1 || f === '1' || f === 'monthly';

function donorWall(live) {
  const rows = [live.donations_with_messages, live.recent_donations]
    .filter(Array.isArray)
    .flat();
  const seen = new Set();
  const wall = [];
  for (const r of rows) {
    const donor = {
      name:
        r.name ||
        r.donor_name ||
        [r.first_name, r.last_name].filter(Boolean).join(' ') ||
        ANONYMOUS,
      // amount_in_shekels FIRST, not `amount`: the wall renders every row with
      // a ₪ prefix, and since donations can now be taken in dollars or euros,
      // `amount` is only shekels for a shekel donation. Preferring it would
      // print a $50 gift as "₪ 50". `amount` stays as the fallback for rows
      // that predate the shekel column being populated.
      amount: wholeShekels(r.amount_in_shekels ?? r.amount ?? r.sum ?? 0),
      message: r.message || r.donation_message || null,
      recurring: isMonthly(r.frequency),
    };
    // Prefer the row's own id; without one, the donor + amount + message is as
    // close to an identity as these rows give us.
    const key = r.id ?? `${donor.name}|${donor.amount}|${donor.message || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    wall.push(donor);
  }
  return wall;
}

// Translate the live Gramsci record (flat snake_case, apiEndpoints.md
// §donationCampaigns: title_he, target_amount, default_amounts_one_time, ...)
// into the shape this page renders. Returns null for a missing or inactive
// campaign — the page then redirects home.
export function normalizeCampaign(live) {
  if (!live || live.is_active === false) return null;

  // Amount buttons compare with strict equality, so normalize to strings
  // (Gramsci stores integer[]).
  const amounts = (arr) => (Array.isArray(arr) && arr.length ? arr.map(String) : null);
  // Money arrives as a numeric string ("1000.00"). null when absent or unparseable.
  //
  // Rounded UP to the whole shekel. The agorot are real — total_raised_shekels
  // sums per-donation values we ourselves write to two decimals (server/fx.js
  // rounds foreign currency to agorot) — but the page renders these figures with
  // a bare toLocaleString, whose default maximumFractionDigits is 3. Without
  // this a total of 21200.5 reads "₪21,200.5" in the headline. Up rather than
  // nearest, so a campaign is never shown as having raised less than it has.
  const money = (v) => {
    const n = v != null ? Number(v) : NaN;
    return Number.isFinite(n) ? Math.ceil(n) : null;
  };

  const descHe = htmlToParagraphs(live.description_he) || [];
  // Arabic is often missing in Gramsci — fall back to Hebrew rather than
  // leaving the ع toggle blank.
  const descAr = htmlToParagraphs(live.description_ar) || descHe;

  return {
    title: {
      he: live.title_he || '',
      ar: live.title_ar || live.title_he || '',
    },
    description: { he: descHe, ar: descAr },
    // Gramsci stores a bare S3 filename we can't resolve client-side, so only
    // use it when it's an absolute URL or a root-relative path we serve ourselves.
    image: /^(https?:\/\/|\/)/.test(live.image || '') ? live.image : undefined,
    goal: money(live.target_amount),
    raised: money(live.total_raised_shekels),
    donorCount: live.donation_count || 0,
    donors: donorWall(live),
    endDate: live.end_date || null,
    onceAmounts: amounts(live.default_amounts_one_time) || DEFAULT_ONCE_AMOUNTS,
    monthlyAmounts: amounts(live.default_amounts_recurring) || DEFAULT_MONTHLY_AMOUNTS,
    showDonorList: live.show_donor_list,
    showTotalRaised: live.show_total_raised,
    showGoal: live.show_goal,
    // Which tab the amount widget opens on: true -> monthly, false/absent -> one-time.
    prioritizeRecurring: live.prioritize_recurring_donations === true,
    // Combined campaign pages only (server/combinedCampaigns.js): the member
    // campaigns shown as boxes under the hero. Absent on an ordinary campaign.
    subCampaigns: Array.isArray(live.sub_campaigns) ? live.sub_campaigns : null,
    // What the thank-you dialog's share buttons send, per language. Either side
    // can be missing in Gramsci: a missing Arabic message falls back to the
    // Hebrew one (as the description does), and with neither the dialog uses its
    // own generic wording.
    shareText: {
      he: htmlToPlainText(live.share_message_he) || htmlToPlainText(live.share_message_ar),
      ar: htmlToPlainText(live.share_message_ar) || htmlToPlainText(live.share_message_he),
    },
  };
}

// Where "הצטרפו אלינו" / "إنضمّوا إلينا" sends the reader. JoinPage is served at
// "/" on every host EXCEPT donate.*, which serves DonatePage there (see App.js)
// — so a campaign running on the donate host has to cross to the join host,
// while everywhere else (including local dev) the link stays in-app.
function joinHref() {
  const host = window.location.hostname;
  const isDonateHost = host.startsWith('donate.') || host.startsWith('www.donate.');
  return isDonateHost ? 'https://join.standing-together.org/' : '/';
}

// The manifesto closes on that call to join; render it as the link into the
// membership page, leaving the rest of the paragraph as plain text.
function ManifestoJoinLink({ text, cta }) {
  return text.split(cta).flatMap((part, i) =>
    i === 0
      ? [part]
      : [
          <a
            className="cmp-em cmp-join-link"
            key={i}
            href={joinHref()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {cta}
          </a>,
          part,
        ]
  );
}

// Description blocks are { tag, html } pairs — html already reduced to inline
// formatting by sanitizeInline() (bold/links authored in Gramsci render as
// markup here), tag is the original p/h1-h6/li so e.g. a heading in Gramsci
// still renders — and styles — as a heading instead of flattening into a <p>.
function DescParagraphs({ paragraphs }) {
  return paragraphs.map(({ tag, html }, i) =>
    createElement(tag, { key: i, dangerouslySetInnerHTML: { __html: html } })
  );
}

export default function CampaignPage() {
  const { campaignName } = useParams();
  // Live Gramsci campaign record, normalized to the page shape (null = unknown → redirect home).
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  // The slug resolved to a petition, not a campaign → redirect to its page.
  const [wrongType, setWrongType] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  // ?donation=recurring / ?donation=once picks the tab explicitly and always
  // wins; with no param the campaign's prioritize_recurring_donations decides,
  // applied once the fetch resolves (see the fetch effect below).
  const donationParam = searchParams.get('donation');
  const initialRecurring = donationParam === 'recurring';

  // Campaign body language toggle (ع / עברית pill in the design). Defaults to Hebrew.
  const [lang, setLang] = useState('he');

  // Mobile hides the "next" button (.cmp-pay-row), so tapping an amount has to
  // advance to the donor form by itself. Must stay pinned to the SAME breakpoint
  // as that rule in App.css, or an amount tap does nothing at the seam.
  const MOBILE_MQ = '(max-width: 767px)';
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_MQ).matches);

  const [recurring, setRecurring] = useState(initialRecurring);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [amount, setAmount] = useState('');
  const [custom, setCustom] = useState(''); // free "other" amount
  const [showForm, setShowForm] = useState(false); // amount widget -> inline donor form
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle'); // idle | error
  const [errorMsg, setErrorMsg] = useState('');
  const [errors, setErrors] = useState({});
  const [igSubmitted, setIgSubmitted] = useState(false); // donor info POSTed -> hosted card iframe
  // "Cover the transaction fee", checked by default — same as donate/join.
  // buildIgPayment sends hidecovermyfee=1 for every page, so without our own
  // checkbox campaign donors would have no way to cover the fee at all.
  const [coverFee, setCoverFee] = useState(true);
  // "Donate anonymously" — campaign-only, since the donor wall is the only
  // place a name is ever shown publicly. Opt-IN (default false): hiding the
  // name has to be the donor's deliberate choice, not something they get by
  // missing a pre-ticked box.
  const [anonymous, setAnonymous] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [openTerms, termsDialog] = useConsentTermsDialog();
  // Token of the donation the thank-you dialog can attach a message to. Set on
  // completion (from the in-page handler, or off the return URL on the PayPal
  // leg). Empty means "no donation to attach to" and the dialog hides the field.
  const [messageToken, setMessageToken] = useState('');

  // Latest donor snapshot for the mount-time completion handler, whose closure
  // would otherwise capture the empty initial form/amount.
  const latestDonor = useRef({});
  latestDonor.current = { form, amount, recurring, anonymous, currency };
  // Guards the completion message against being processed twice.
  const completedDonationRef = useRef(false);
  // Per-attempt id linking the 'initiated' donor details to the completion
  // that comes back through IsraelGives' successurl. Set in onSubmit.
  const paymentTokenRef = useRef('');
  // Donor-wall carousel, scrolled by the left/right arrow buttons.
  const donorTrackRef = useRef(null);
  // The mobile copy inside the card is a separate track, so it needs its own ref.
  const donorTrackMobileRef = useRef(null);
  // On mobile the amount widget is pinned to the bottom of the viewport, so the
  // page has to reserve its height as bottom padding or the pinned bar would
  // cover the tail of the footer. Measured live: the widget grows/shrinks with
  // the one-time/monthly toggle.
  const widgetRef = useRef(null);
  const [widgetH, setWidgetH] = useState(0);
  const scrollDonors = (dir, ref = donorTrackRef) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 480), behavior: 'smooth' });
  };

  useEffect(() => {
    const el = widgetRef.current;
    if (!isMobile || !el) {
      setWidgetH(0);
      return undefined;
    }
    const measure = () => setWidgetH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // `campaign` is in the deps because the widget only exists once the fetch
    // resolves — without it the first (empty) pass would be the last measurement.
  }, [isMobile, showForm, recurring, campaign]);

  const countdown = useCountdown(campaign && campaign.endDate);

  // Lets the hosted payment frame grow to fit its content (see the hook).
  const igFrameRef = useIgFrameHeight();
  useBitRedirect(); // mobile bit: their framed page hands us the link to open

  const { sendNow: sendAbandonedForm } = useAbandonedForm({
    form,
    page: `קמפיין: ${campaignName || ''}`,
    donationType: recurring ? 'חודשי' : 'חד פעמי',
    amount,
    submitted: completedDonationRef.current,
    activity: igSubmitted,
  });

  // Keep the mobile flag in sync when the viewport crosses the breakpoint.
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Land at the top on load/refresh, ignoring any restored scroll position.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  // Fetch the live campaign from Gramsci via our backend, which resolves the
  // slug to its campaign id and holds the api_key. Resolves null for unknown
  // slugs or fetch failures → the page redirects home.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // CAMPAIGN picks the campaigns lookup on the backend. It is also what an
    // omitted hint defaults to, so this is belt and braces — but stating it
    // keeps the two pages symmetrical, and stops the default being the only
    // thing holding the campaign path up.
    fetchGramsciProject(campaignName, CAMPAIGN).then((live) => {
      if (cancelled) return;
      // A petition reached at the campaign URL: the record says what it is, so
      // hand it to the page that renders that (see PetitionPage for the mirror
      // image). normalizeCampaign() would only turn a petition into a campaign
      // with everything missing.
      setWrongType(isPetition(live));
      const next = normalizeCampaign(live);
      setCampaign(next);
      // Gramsci decides the default tab per campaign; an explicit ?donation=
      // param overrides it, so only apply the flag when there is no param.
      if (next && !donationParam) setRecurring(next.prioritizeRecurring);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [campaignName, donationParam]);

  // Return the page to its landing state (amount widget, empty donor form) and
  // scroll back up to it. Defined here, above the early returns, because the
  // completion handler below closes over it on the very first render.
  const backToCampaign = () => {
    setShowForm(false);
    setIgSubmitted(false);
    setForm(emptyForm);
    setAmount('');
    setCustom('');
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // A donor who paid through a method that LEFT the iframe (PayPal) never got
  // the in-page success flow — ig-complete.html sends them back here with
  // ?donated=1 instead. Their donation is already recorded server-side by then;
  // this is purely the confirmation they'd otherwise never see.
  useEffect(() => {
    if (searchParams.get('donated') !== '1') return;
    completedDonationRef.current = true; // never chase them as an abandoned form
    // ig-complete.html passes the attempt token back so this donor can leave a
    // donor-wall message too — they never had an in-page completion to carry it.
    setMessageToken(searchParams.get('t') || '');
    setShowThankYou(true);
    const next = new URLSearchParams(searchParams);
    next.delete('donated'); // so a refresh doesn't reopen the dialog
    next.delete('t');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // The IsraelGives frame redirects to /ig-complete.html (same-origin) on a
  // successful charge, which postMessages us. Swap to our own success screen.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === 'ig-donation-complete') {
        // Answer first, before any early return: ig-complete.html takes the
        // whole tab back to the campaign if nothing acknowledges it, and a
        // duplicate message is still proof that this page is handling it.
        if (e.source) e.source.postMessage({ type: 'ig-donation-ack' }, window.location.origin);
        if (completedDonationRef.current) return;
        completedDonationRef.current = true;
        // The completion is reported to the backend by ig-complete.html, not
        // here — that's the only path that also survives a payment method
        // which left the iframe (PayPal). This handler owns the UI and the
        // conversions; duplicating the report would just be a second write.
        // The currency the donor chose, not a hardcoded ILS. Read off the ref
        // because this handler is mounted once and its closure would otherwise
        // hold the initial value.
        const paidCurrency = latestDonor.current.currency || DEFAULT_CURRENCY;
        const paidValue = resolvePurchaseValue(latestDonor.current.total, e.data.sum);
        if (window.fbq) window.fbq('track', 'Purchase', { value: paidValue, currency: paidCurrency });
        if (window.gtag) {
          window.gtag('event', 'conversion', { send_to: 'AW-718777808/kPpUCPHXhu0bENDb3tYC', transaction_id: e.data.donationId || '' });
        }
        // GA4 + the other GTM purchase tags — see analytics.js for why this isn't a gtag() call.
        trackPurchase({ transactionId: e.data.donationId, value: paidValue, currency: paidCurrency });
        setIgSubmitted(false);
        setStatus('idle');
        // Land the donor back on the campaign page itself — the thank-you
        // dialog over it IS the confirmation, so there's no success screen to
        // swap in. Also clears the donor form, so a second gift starts fresh.
        backToCampaign();
        // Read before backToCampaign()'s state resets land — this is the
        // donation the dialog's message field will attach to.
        setMessageToken(paymentTokenRef.current || '');
        setShowThankYou(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [campaignName]);

  // Once donor details are submitted, POST the hidden form into the frame so
  // IsraelGives shows the hosted card fields. This page uses a SINGLE responsive
  // layout (unlike donate/join's dual desktop+mobile trees), so there is exactly
  // one hosted form/iframe — just submit it.
  useEffect(() => {
    if (!igSubmitted) return;
    const el = document.querySelector('form.mf-hosted-post');
    if (el) el.submit();
  }, [igSubmitted]);

  // Still fetching the campaign JSON: a spinner, not the page shell, so nothing
  // flashes as not-found. A combined page waits on several Gramsci lookups.
  if (loading) {
    return (
      <div className="cmp-loading" role="status">
        <span className="cmp-spinner" aria-hidden="true" />
        <span className="cmp-loading-label">טוען… جارٍ التحميل…</span>
      </div>
    );
  }

  // Checked before the not-found redirect below: a petition is a perfectly good
  // record, it just isn't this page's.
  if (wrongType) return <Navigate to={`/petitions/${campaignName}`} replace />;

  // Unknown/inactive campaign (or Gramsci unreachable): redirect to the home
  // page (donate or join, per host). Hooks above still run first so
  // rules-of-hooks are respected.
  if (!campaign) return <Navigate to="/" replace />;

  // The campaign's own ladders come out of Gramsci and are shekel amounts, so
  // they're used for ILS only; a foreign currency gets round numbers of its own
  // (currencies.js) rather than a converted 18 → 4.83.
  const amounts = presetAmounts(currency, recurring, recurring ? campaign.monthlyAmounts : campaign.onceAmounts);
  const cur = currencyMeta(currency);
  // An "other" amount below this is invalid (red border) and blocks the next
  // button, matching the donate/join pages.
  const minAmount = cur.min;
  // Fee is computed off the chosen amount but only added to the sum sent to
  // IsraelGives when the donor opts in — the displayed donation amount stays
  // as chosen. Denominated in the donation's own currency.
  const feeAmount = calcFeeCoverAmount(amount, currency);
  const totalAmount = coverFee ? Math.round((Number(amount) + feeAmount) * 100) / 100 : Number(amount);
  latestDonor.current.total = totalAmount;
  // Gramsci's visibility flags; raised comes from the static JSON only, so a
  // live-only campaign simply has no total/progress to show.
  const showRaised = campaign.showTotalRaised !== false && campaign.raised != null;
  const showGoal = campaign.showGoal !== false && campaign.goal != null;
  // rawPct is the displayed percentage, uncapped — an overfunded campaign reads
  // "156%". See displayPct for how it rounds.
  const rawPct = showGoal ? displayPct(campaign.raised, campaign.goal) : 0;
  // The bar can't visually overflow its track, so `pct` (used for the single-fill
  // width) is separately capped at 100.
  const pct = Math.min(100, rawPct);
  // Past the goal the track stops meaning "the goal" and starts meaning "what
  // was raised": the whole bar is rawPct, of which the goal is this share. The
  // rest is the overshoot, drawn in gold beyond it (see .cmp-progress--over).
  const overGoal = rawPct > 100;
  const goalShare = overGoal ? (100 / rawPct) * 100 : 100;
  // Below ~16% of the track the gold strip is too narrow to hold a label. That
  // is every campaign between 101% and 119%, so it needs to degrade rather than
  // clip: the total moves onto the purple and the "100%" marker steps aside,
  // leaving a full bar with a gold sliver at its leading edge.
  const roomForOverLabel = 100 - goalShare >= 16;

  const setField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    // Editing donor fields invalidates the already-POSTed iframe (it holds the
    // old values), so drop back to the form; re-submitting reloads it fresh.
    if (igSubmitted) setIgSubmitted(false);
  };

  const customNum = Number(custom);
  const customInvalid = custom.trim() !== '' && (!Number.isFinite(customNum) || customNum < minAmount);
  const customValid = custom.trim() !== '' && !customInvalid;

  const selectType = (isMonthly) => {
    setRecurring(isMonthly);
    // The currency survives the switch: every currency is offered on both tabs
    // now (see currencies.js). Only the amount is cleared, because the monthly
    // ladder is a different one.
    setAmount('');
    setCustom('');
  };

  // Presets differ per currency, so a stale selection would highlight an amount
  // that isn't on the new ladder.
  const selectCurrency = (code) => {
    setCurrency(code);
    setAmount('');
    setCustom('');
  };

  // "הבא לתשלום": carry the chosen (or custom) amount into the inline donor form.
  // `preset` is passed when a mobile amount tap advances straight to the form:
  // setAmount() from that same tap hasn't been applied to `amount` yet, so the
  // tapped value has to come in directly. Never called with an event object —
  // every onClick that uses this wraps it in an arrow.
  const goToPayment = (preset) => {
    setIgSubmitted(false);
    setErrors({});
    if (preset) {
      setAmount(preset);
      setCustom('');
      setShowForm(true);
      return;
    }
    if (custom.trim() !== '') {
      if (customInvalid) return;
      setAmount(custom.trim());
      setShowForm(true);
      return;
    }
    if (!amount || Number(amount) <= 0) return;
    setShowForm(true);
  };

  // Bring the donor back from the inline donor form to the amount step.
  const backToAmount = () => {
    // Leaving the donor form is an SPA navigation — no unload fires, so the
    // hook's beforeunload/idle triggers never see it. Capture what was typed
    // before the fields go out of view (matches JoinPage's backToAmount).
    sendAbandonedForm();
    setShowForm(false);
    setIgSubmitted(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return;
    const found = validateForm(form, currency);
    setErrors(found);
    if (Object.keys(found).length) return;
    setErrorMsg('');
    // One token per attempt, generated here and reused by the hidden POST form
    // below — it's what lets the backend match this donor to the completion.
    paymentTokenRef.current = newPaymentToken();
    logDonationEvent({
      // Attributes the donation to THIS campaign in Gramsci — without it the
      // record falls back to the global GRAMSCI_CAMPAIGN_NAME, the same label
      // a plain /donate gift gets.
      stage: 'initiated', page: `קמפיין: ${campaignName || ''}`, campaignName,
      token: paymentTokenRef.current,
      firstName: form.firstName, lastName: form.lastName, email: form.email,
      phone: form.phone, city: form.city, amount, recurring, coverFee,
      frequency: recurring ? '2' : '1',
      // No `message` here any more — it's collected in the thank-you dialog
      // once the charge has gone through, and attached in a follow-up call.
      anonymous,
      // Rejoined server-side with the completion, which carries none of it.
      // The campaign's progress bar is shekel-denominated, so `currency` is
      // also what lets the backend convert this donation into the total.
      currency,
      street: form.street, zipCode: form.zipCode, country: countryName(form.countryId),
      ...readUtmParams(),
    });
    setIgSubmitted(true);
  };

  // ---- Amount widget (matches img.png / img_1.png) ----
  const renderAmountWidget = () => (
    <div className="donate-widget donate-widget--inline cmp-widget" ref={widgetRef}>
      <div className="donate-toggle">
        <button type="button" className={!recurring ? 'active' : ''} onClick={() => selectType(false)}>
          <span className="bi-he">{d.types.once.he}</span>{' '}
          <span className="bi-ar">{d.types.once.ar}</span>
        </button>
        <button type="button" className={recurring ? 'active' : ''} onClick={() => selectType(true)}>
          <span className="bi-he">{d.types.monthly.he}</span>{' '}
          <span className="bi-ar">{d.types.monthly.ar}</span>
        </button>
      </div>
      <CurrencyPicker
        id="campaign-currency"
        value={currency}
        onChange={selectCurrency}
      />
      <div className="amount-grid">
        {amounts.map((a) => (
          <button
            type="button"
            key={a}
            className={`amount ${!custom && amount === a ? 'active' : ''}`}
            // Mobile has no visible "next" button, so the tap advances on its own.
            onClick={() => { setAmount(a); setCustom(''); if (isMobile) goToPayment(a); }}
          >
            <span className="amount-value">{formatAmount(a, currency)}</span>
            {recurring ? (
              <span className="per-month">
                <span className="bi-he">{d.perMonth.he}</span>{' '}
                <span className="bi-ar">{d.perMonth.ar}</span>
              </span>
            ) : null}
          </button>
        ))}
        <div className="cmp-other-wrap">
          <input
            className={`amount donate-other ${custom.trim() !== '' ? 'active' : ''} ${customInvalid ? 'donate-other--invalid' : ''}`}
            type="number"
            inputMode="numeric"
            enterKeyHint="go"
            min={minAmount}
            placeholder={`אחר آخر (${cur.symbol})`}
            value={custom}
            onChange={(e) => { setCustom(e.target.value); setAmount(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && customValid) { e.preventDefault(); goToPayment(); } }}
          />
          {/* Mobile only (styled in @media <768px): the submit button is hidden there,
              so this inline icon appears once a custom amount is typed to submit it. */}
          {custom.trim() !== '' && (
            <button
              type="button"
              className="cmp-other-go"
              onClick={() => goToPayment()}
              disabled={!customValid}
              aria-label="המשך التالي"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                <path d="M13 5l-7 7 7 7M6 12h13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="cmp-pay-row">
        <button type="button" className="donate-next" onClick={() => goToPayment()} disabled={!amount && !customValid}>
          <span className="bi-he">{d.next.he}</span>{' '}
          <span className="bi-ar">{d.next.ar}</span>
        </button>
        <a className="cmp-whatsapp" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
          <svg viewBox="0 0 54 55" width="36" height="36" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M46.1328 7.897C43.6446 5.38441 40.6811 3.39218 37.4151 2.0364C34.1491 0.680629 30.646 -0.0115689 27.1098 0.000146255C12.293 0.000146255 0.217096 12.0761 0.217096 26.8929C0.217096 31.6418 1.4654 36.2551 3.79918 40.3257L0 54.2741L14.2469 50.5292C18.1818 52.673 22.6051 53.8127 27.1098 53.8127C41.9266 53.8127 54.0026 41.7368 54.0026 26.92C54.0026 19.7287 51.2074 12.9716 46.1328 7.897ZM27.1098 49.2537C23.0936 49.2537 19.1587 48.1683 15.7123 46.133L14.8982 45.6445L6.43146 47.8698L8.68383 39.6201L8.14109 38.7789C5.9092 35.2159 4.72434 31.0971 4.72183 26.8929C4.72183 14.5727 14.7625 4.53202 27.0827 4.53202C33.0528 4.53202 38.6702 6.8658 42.8764 11.0992C44.9595 13.1721 46.6102 15.6379 47.7329 18.3538C48.8556 21.0696 49.4278 23.9813 49.4164 26.92C49.4707 39.2402 39.43 49.2537 27.1098 49.2537ZM39.3757 32.5374C38.6973 32.2117 35.3866 30.5835 34.7896 30.3393C34.1654 30.1222 33.7312 30.0136 33.2699 30.6649C32.8086 31.3433 31.5331 32.863 31.1532 33.2972C30.7733 33.7585 30.3663 33.8128 29.6878 33.46C29.0094 33.1344 26.8385 32.4017 24.2876 30.1222C22.2794 28.3311 20.9497 26.133 20.5427 25.4546C20.1628 24.7762 20.4884 24.4234 20.8412 24.0706C21.1397 23.7721 21.5196 23.2837 21.8453 22.9037C22.1709 22.5238 22.3066 22.2253 22.5237 21.7911C22.7408 21.3298 22.6322 20.9499 22.4694 20.6242C22.3066 20.2986 20.9497 16.9879 20.407 15.631C19.8643 14.3285 19.2944 14.4913 18.8873 14.4641H17.5848C17.1234 14.4641 16.4179 14.627 15.7937 15.3054C15.1967 15.9838 13.4599 17.612 13.4599 20.9227C13.4599 24.2335 15.8751 27.4356 16.2008 27.8698C16.5264 28.3311 20.9497 35.1154 27.6797 38.019C29.2808 38.7246 30.5291 39.1317 31.506 39.4302C33.1071 39.9458 34.5725 39.8643 35.7394 39.7015C37.042 39.5116 39.7285 38.0733 40.2713 36.4994C40.8411 34.9254 40.8411 33.5957 40.6512 33.2972C40.4612 32.9987 40.0542 32.863 39.3757 32.5374Z" />
          </svg>
        </a>
      </div>
    </div>
  );

  // Hidden POST form + iframe: IsraelGives renders the hosted card fields inside.
  const renderHostedPayment = (frameName) => {
    const { action, fields } = buildIgPayment({
      // `street` is our label for what IsraelGives calls `address`.
      profile: { ...form, address: form.street },
      amount: totalAmount, // includes the covered fee when the donor opted in
      currency,
      frequency: recurring ? '2' : '1',
      token: paymentTokenRef.current,
      returnPath: window.location.pathname, // where to land a donor who came back off-frame
    });
    return (
      <div className="mf-hosted">
        <form method="POST" action={action} target={frameName} className="mf-hosted-post">
          {Object.entries(fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
        <iframe ref={igFrameRef} className="mf-iframe" title="IsraelGives" name={frameName} />
        <p className="member-consent">
          <span className="bi-he">{d.consent.he}<TermsLink onOpen={openTerms}>{d.consent.heTerms}</TermsLink></span>
          <span className="bi-ar">{d.consent.ar}<TermsLink onOpen={openTerms}>{d.consent.arTerms}</TermsLink></span>
        </p>
        <button type="button" className="mf-hosted-edit" onClick={() => setIgSubmitted(false)}>
          <span className="bi-he">{d.back.he}</span>{' '}
          <span className="bi-ar">{d.back.ar}</span>
        </button>
      </div>
    );
  };

  const renderDonorFields = (frameName) => (
    <>
      <div className="mf-row">
        <label className="mf-field">
          <span className="mf-label">{d.fields.firstName.he} {d.fields.firstName.ar} <em>*</em></span>
          <input autoComplete="given-name" className={errors.firstName ? 'mf-invalid' : undefined} value={form.firstName} onChange={setField('firstName')} />
          {errors.firstName && <span className="mf-error">{errors.firstName}</span>}
        </label>
        <label className="mf-field">
          <span className="mf-label">{d.fields.lastName.he} {d.fields.lastName.ar} <em>*</em></span>
          <input autoComplete="family-name" className={errors.lastName ? 'mf-invalid' : undefined} value={form.lastName} onChange={setField('lastName')} />
          {errors.lastName && <span className="mf-error">{errors.lastName}</span>}
        </label>
      </div>
      <div className="mf-row">
        <label className="mf-field">
          <span className="mf-label">{d.fields.city.he} {d.fields.city.ar} <em>*</em></span>
          <input autoComplete="address-level2" className={errors.city ? 'mf-invalid' : undefined} value={form.city} onChange={setField('city')} />
          {errors.city && <span className="mf-error">{errors.city}</span>}
        </label>
        <label className="mf-field">
          <span className="mf-label">{d.fields.phone.he} {d.fields.phone.ar} <em>*</em></span>
          <input type="tel" autoComplete="tel" className={errors.phone ? 'mf-invalid' : undefined} value={form.phone} onChange={setField('phone')} />
          {errors.phone && <span className="mf-error">{errors.phone}</span>}
        </label>
      </div>
      <label className="mf-field">
        <span className="mf-label">{d.fields.email.he} {d.fields.email.ar} <em>*</em></span>
        <input type="email" autoComplete="email" className={errors.email ? 'mf-invalid' : undefined} value={form.email} onChange={setField('email')} />
        {errors.email && <span className="mf-error">{errors.email}</span>}
      </label>
      {/* The donor-wall message used to be a field here. It's now asked for in
          the thank-you dialog after the charge, so nothing optional stands
          between the donor and paying. */}

      {needsAddress(currency) && (
        <ForeignAddressFields form={form} errors={errors} setField={setField} />
      )}

      <div className="mf-divider" aria-hidden="true">
        <span className="mf-divider-dot" />
      </div>

      {!igSubmitted ? (
        <>
          {status === 'error' && <p className="join-error member-error">{errorMsg}</p>}
          <div className="member-secure">
            <img className="member-secure-badge" src="/images/pci-dss.webp" alt="PCI DSS Compliant" width="130" loading="lazy" />
            <p className="member-secure-text">
              <span className="bi-he">{d.secureNotice.he}</span>
              <span className="bi-ar">{d.secureNotice.ar}</span>
            </p>
          </div>
          <label className="mf-check mf-cover-fee">
            <input
              type="checkbox"
              checked={coverFee}
              onChange={(e) => setCoverFee(e.target.checked)}
            />
            <span>
              <span className="bi-he">{d.coverFee.he.replace('{amount}', formatAmount(feeAmount, currency))}</span>
              <span className="bi-ar">{d.coverFee.ar.replace('{amount}', formatAmount(feeAmount, currency))}</span>
            </span>
          </label>
          <label className="mf-check mf-anonymous">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
            />
            <span>
              <span className="bi-he">{d.anonymous.he}</span>
              <span className="bi-ar">{d.anonymous.ar}</span>
            </span>
          </label>
          <button className="member-submit" type="submit" disabled={!amount}>
            <span className="bi-he">{d.pay.he}</span>
            <span className="bi-ar">{d.pay.ar}</span>
          </button>
          <p className="member-consent">
            <span className="bi-he">{d.consent.he}<TermsLink onOpen={openTerms}>{d.consent.heTerms}</TermsLink></span>
            <span className="bi-ar">{d.consent.ar}<TermsLink onOpen={openTerms}>{d.consent.arTerms}</TermsLink></span>
          </p>
        </>
      ) : (
        renderHostedPayment(frameName)
      )}
    </>
  );

  const renderDonateForm = (frameName) => (
    <form className="member-form donate-inline-form cmp-form" onSubmit={onSubmit} noValidate>
      <button type="button" className="donate-inline-back" onClick={backToAmount}>
        <span aria-hidden="true">»</span>
        <span className="donate-inline-back-text">
          <span className="bi-he">{d.back.he}</span>
          <span className="bi-ar">{d.back.ar}</span>
        </span>
      </button>
      <div className="donate-inline-header">
        <h2 className="donate-inline-title">
          <span className="bi-he">{campaign.title.he}</span>
          <span className="bi-ar">{campaign.title.ar}</span>
        </h2>
        <div className="donate-inline-amount">
          {recurring ? 'תשלום חודשי בסך' : d.paymentOf.he} <span className="donate-inline-amount-val">{formatAmount(amount, currency)}</span>
        </div>
      </div>
      {renderDonorFields(frameName)}
    </form>
  );

  const aside = d.aside;

  // Share message for the thank-you dialog: the campaign's own text from
  // Gramsci per language, or the generic wording with the campaign title
  // filled in where Gramsci has none.
  const shareText = {
    he: campaign.shareText.he || share.campaign.defaultText.he.replace('{title}', campaign.title.he),
    ar: campaign.shareText.ar || share.campaign.defaultText.ar.replace('{title}', campaign.title.ar),
  };

  return (
    <div
      className={`campaign ${showForm ? 'campaign--paying' : ''} ${igSubmitted ? 'campaign--checkout' : ''}`}
      dir="rtl"
      lang="he"
      style={widgetH ? { '--cmp-widget-h': `${widgetH}px` } : undefined}
    >
      {termsDialog}
      <ThankYouModal
        open={showThankYou}
        onClose={() => setShowThankYou(false)}
        variant="campaign"
        shareText={shareText}
        // What the share buttons link to: this campaign's public page.
        shareUrl={`${window.location.origin}/${campaignName}`}
        // Only offered when we know which donation to attach the message to.
        onSubmitMessage={
          messageToken
            ? (message) => submitDonationMessage({ token: messageToken, campaignName, message })
            : undefined
        }
      />

      {/* ---- Hero: campaign media/copy + donation card. Grid areas place the
             card on the left (desktop) and reflow to image → card → copy (mobile). ---- */}
      <section className={`cmp-hero${campaign.subCampaigns?.length ? ' cmp-hero--combined' : ''}`}>
        {/* Media top: image + title (right column desktop, top on mobile) */}
        <div className="cmp-media-top">
          {campaign.image && <img className="cmp-hero-img" src={campaign.image} alt="" />}
          <h1 className="cmp-title">
            <span className="bi-he">{campaign.title.he}</span>
            <span className="bi-ar">{campaign.title.ar}</span>
          </h1>
        </div>

        {/* Donation card (left on desktop, between image and copy on mobile) */}
        <aside className="cmp-card">
          {/* A completed donation doesn't swap this card for a success screen:
              the donor is put back here, with the thank-you dialog over it. */}
          <div className="cmp-stats">
            {showRaised && (
              <>
                <p className="cmp-raised-label">
                  <span className="bi-he">עד כה גויסו تم جمع حتى الآن</span>
                </p>
                <div className="cmp-raised">₪{campaign.raised.toLocaleString('en-US')}</div>
                <p className="cmp-donors">
                  <span className="bi-he">בזכות <span className="cmp-donor-num">{campaign.donorCount}</span> תורמים ותורמות</span>{' '}
                  <span className="bi-ar">بفضل <span className="cmp-donor-num">{campaign.donorCount}</span> متبرعاً</span>
                </p>
              </>
            )}
            {showGoal && (
              <>
                {showRaised && (
                  <div
                    className={`cmp-progress${overGoal ? ' cmp-progress--over' : ''}`}
                    role="progressbar"
                    aria-valuenow={rawPct}
                    aria-valuemin={0}
                    aria-valuemax={Math.max(100, rawPct)}
                    aria-valuetext={`${rawPct}%`}
                  >
                    {overGoal ? (
                      <>
                        {/* RTL: the first child sits at the bar's right, where it
                            starts filling. Purple is everything up to the goal;
                            the gold strip beyond it is the overshoot. */}
                        <div className="cmp-progress-fill cmp-progress-fill--goal" style={{ width: `${goalShare}%` }}>
                          {roomForOverLabel && <span className="cmp-progress-pct cmp-progress-pct--goal">100%</span>}
                          {!roomForOverLabel && <span className="cmp-progress-pct cmp-progress-pct--total-inside">{rawPct}%</span>}
                        </div>
                        <div className="cmp-progress-fill cmp-progress-fill--extra" style={{ width: `${100 - goalShare}%` }}>
                          {roomForOverLabel && <span className="cmp-progress-pct cmp-progress-pct--total">{rawPct}%</span>}
                        </div>
                      </>
                    ) : (
                      <div className="cmp-progress-fill" style={{ width: `${pct}%` }}>
                        {pct > 10 && (
                          // A bar that reached exactly its goal takes the gold
                          // the overshoot bars use, rather than the white of a
                          // bar still climbing — 100% is the milestone, not a
                          // waypoint.
                          <span className={`cmp-progress-pct cmp-progress-pct--over${rawPct === 100 ? ' cmp-progress-pct--full' : ''}`}>{rawPct}%</span>
                        )}
                      </div>
                    )}
                    {/* At 10% or less the fill is too narrow to hold the label — it
                        would be cramped or clipped by the track's overflow:hidden.
                        Render it on the white track instead, just past the fill's
                        leading edge, where purple-on-white reads clearly. The bar
                        fills right-to-left (RTL), so offsetting from the right puts
                        it to the LEFT of the fill. */}
                    {pct <= 10 && (
                      <span
                        className="cmp-progress-pct cmp-progress-pct--outside"
                        style={{ right: `calc(${pct}% + 8px)` }}
                      >
                        {rawPct}%
                      </span>
                    )}
                  </div>
                )}
                <p className="cmp-goal">
                  <span className="bi-he">מתוך</span>{' '}
                  <span className="bi-ar">من أصل</span>{' '}
                  {/* dir=ltr keeps the ₪ on the LEFT of the digits; in the
                      surrounding RTL paragraph bidi would otherwise be free
                      to flip it to the right of the number. */}
                  <span dir="ltr">₪{campaign.goal.toLocaleString('en-US')}</span>
                </p>
              </>
            )}
          </div>

          {showForm ? renderDonateForm('ig-frame-campaign') : (
            <>
              {renderAmountWidget()}
              {countdown && (
                <div className="cmp-countdown">
                  <span className="cmp-countdown-lines">
                    <span className="bi-he">נותרו {countdown.days} ימים {countdown.hours} שעות ו-{countdown.minutes} דקות</span>
                    <span className="bi-ar">تبقى {countdown.days} يومًا و{countdown.hours} ساعة و{countdown.minutes} دقيقة</span>
                  </span>
                </div>
              )}
              {/* Combined campaign pages, phones: the member-campaign carousel
                  sits in the card, above the language toggle, instead of
                  under the hero (see the desktop placement below). */}
              {isMobile && (
                <OtherCampaigns campaigns={campaign.subCampaigns} className="cmp-others--in-card" />
              )}
              {/* Mobile-only copy of the language toggle + description: on
                  phones the media body (and both of those) is hidden, so
                  they ride inside the card, directly under the countdown.
                  Desktop keeps the originals in .cmp-media-body. */}
              <div className="cmp-lang-wrap cmp-lang-wrap--mobile">
                <span className="cmp-lang-side">عر</span>
                <button
                  type="button"
                  className={`cmp-lang ${lang === 'ar' ? 'cmp-lang--ar' : 'cmp-lang--he'}`}
                  role="switch"
                  aria-checked={lang === 'ar'}
                  aria-label="שפה / اللغة"
                  onClick={() => setLang(lang === 'he' ? 'ar' : 'he')}
                >
                  <span className="cmp-lang-knob" aria-hidden="true" />
                </button>
                <span className="cmp-lang-side">עב</span>
              </div>
              {/* Both languages stay mounted in the same grid cell (see the
                  desktop block below for the full note), so the card keeps
                  the taller text's height and the ع/עב toggle can't make
                  the page jump. */}
              <div className="cmp-desc cmp-desc--mobile">
                <div className="cmp-desc-lang" data-active={lang === 'he'} lang="he" dir="rtl">
                  <DescParagraphs paragraphs={campaign.description.he} />
                </div>
                <div className="cmp-desc-lang cmp-desc--ar" data-active={lang === 'ar'} lang="ar" dir="rtl">
                  <DescParagraphs paragraphs={campaign.description.ar} />
                </div>
              </div>
              {/* Mobile-only donor list: the full donor wall is hidden on
                  phones, so the carousel lives here — centered title, then
                  arrows flanking a track whose cards are half the width, so
                  the next donor peeks in from the side. */}
              {campaign.showDonorList !== false && campaign.donors && campaign.donors.length > 0 && (
                <div className="cmp-donors-mobile">
                  <h2 className="cmp-section-title">
                    <span className="bi-he">תודה לתורמים ולתורמות שלנו</span>
                    <span className="bi-ar">نشكر المتبرعين والمتبرعات</span>
                  </h2>
                  <div className="cmp-donors-mobile-row">
                    <button type="button" className="cmp-donor-arrow" aria-label="הקודם" onClick={() => scrollDonors(1, donorTrackMobileRef)}>▶</button>
                    <div className="cmp-donor-track" ref={donorTrackMobileRef}>
                      {campaign.donors.map((donor, i) => (
                        <div className="cmp-donor-card" key={i}>
                          <div className="cmp-donor-top">
                            <span className="cmp-donor-name" title={donor.name}>{donor.name}</span>
                            <span className="cmp-donor-amount">₪ {donor.amount}</span>
                          </div>
                          {donor.recurring && (
                            <span className="cmp-donor-recurring">
                              <span className="bi-he">תמיכה חודשית</span>
                              <span className="bi-ar">دعم شهري</span>
                            </span>
                          )}
                          {donor.message && <p className="cmp-donor-msg">{donor.message}</p>}
                        </div>
                      ))}
                    </div>
                    <button type="button" className="cmp-donor-arrow" aria-label="הבא" onClick={() => scrollDonors(-1, donorTrackMobileRef)}>◀</button>
                  </div>
                </div>
              )}
            </>
          )}
        </aside>

        {/* Media body: language toggle + description (right column desktop,
            below the card on mobile) */}
        <div className="cmp-media-body">
          <div className="cmp-lang-wrap">
            <span className="cmp-lang-side">عر</span>
            <button
              type="button"
              className={`cmp-lang ${lang === 'ar' ? 'cmp-lang--ar' : 'cmp-lang--he'}`}
              role="switch"
              aria-checked={lang === 'ar'}
              aria-label="שפה / اللغة"
              onClick={() => setLang(lang === 'he' ? 'ar' : 'he')}
            >
              <span className="cmp-lang-knob" aria-hidden="true" />
            </button>
            <span className="cmp-lang-side">עב</span>
          </div>
          {/* Both languages are rendered and stacked in the same grid cell, so
              the block always reserves the height of the LONGER text and the
              ع/עב toggle never reflows the page. Only the active one is
              visible (visibility: hidden also hides the other from screen
              readers, so aria-hidden isn't needed). */}
          <div className="cmp-desc">
            <div className="cmp-desc-lang" data-active={lang === 'he'} lang="he" dir="rtl">
              <DescParagraphs paragraphs={campaign.description.he} />
            </div>
            <div className="cmp-desc-lang cmp-desc--ar" data-active={lang === 'ar'} lang="ar" dir="rtl">
              <DescParagraphs paragraphs={campaign.description.ar} />
            </div>
          </div>
        </div>
      </section>

      {/* ---- Combined campaign pages only: the member campaigns. On phones
             they render inside the card instead (above the language toggle). ---- */}
      {!isMobile && <OtherCampaigns campaigns={campaign.subCampaigns} />}

      {/* ---- Donor wall: lavender band, title + arrows on the right, cards carousel ---- */}
      {campaign.showDonorList !== false && campaign.donors && campaign.donors.length > 0 && (
        <section className="cmp-donorwall">
          <div className="cmp-donorwall-row">
            <h2 className="cmp-section-title">
              <span className="bi-he">תודה לתורמים ולתורמות שלנו</span>
              <span className="bi-ar">نشكر المتبرعين والمتبرعات</span>
            </h2>
            <button type="button" className="cmp-donor-arrow" aria-label="הקודם" onClick={() => scrollDonors(1)}>▶</button>
            <div className="cmp-donor-track" ref={donorTrackRef}>
              {campaign.donors.map((donor, i) => (
                <div className="cmp-donor-card" key={i}>
                  <div className="cmp-donor-top">
                    <span className="cmp-donor-name" title={donor.name}>{donor.name}</span>
                    <span className="cmp-donor-amount">₪ {donor.amount}</span>
                  </div>
                  {donor.recurring && (
                    <span className="cmp-donor-recurring">
                      <span className="bi-he">תמיכה חודשית</span>
                      <span className="bi-ar">دعم شهري</span>
                    </span>
                  )}
                  {donor.message && <p className="cmp-donor-msg">{donor.message}</p>}
                </div>
              ))}
            </div>
            <button type="button" className="cmp-donor-arrow" aria-label="הבא" onClick={() => scrollDonors(-1)}>◀</button>
          </div>
        </section>
      )}

      {/* ---- Footer band: manifesto + bank/Bit + stacked CTAs (matches img.png) ---- */}
      <section className="cmp-footer">
        <div className="cmp-footer-inner">
          <p className="cmp-manifesto">
            <span className="bi-he">
              <ManifestoJoinLink text={aside.paragraphs[2].he} cta="הצטרפו אלינו" />
            </span>
            <span className="bi-ar cmp-manifesto-ar">
              <ManifestoJoinLink text={aside.paragraphs[2].ar} cta="إنضمّوا إلينا" />
            </span>
          </p>

          <div className="cmp-bank">
            {/* Bit (left) */}
            <div className="cmp-bank-col">
              <p className="cmp-bank-title bi-he">{aside.bitTitle.he}</p>
              <p className="cmp-bank-title bi-ar">{aside.bitTitle.ar}</p>
              <p className="cmp-bank-line bi-he">{aside.bitLine.he}</p>
              <p className="cmp-bank-line bi-ar">
                {(() => {
                  // Split the phone number off the Arabic line: put it on its own
                  // row and render it LTR so the digits/hyphen don't reverse in RTL.
                  const s = aside.bitLine.ar;
                  const idx = s.search(/[0-9]/);
                  if (idx === -1) return s;
                  return (
                    <>
                      {s.slice(0, idx).trim()}
                      <br />
                      <span dir="ltr">{s.slice(idx).trim()}</span>
                    </>
                  );
                })()}
              </p>
            </div>
            {/* Bank transfer (right) */}
            <div className="cmp-bank-col">
              <p className="cmp-bank-title bi-he">{aside.bankTitle.he}</p>
              <p className="cmp-bank-title bi-ar">{aside.bankTitle.ar}</p>
              <div className="cmp-bank-account">
                {aside.bankLines.map((l, i) => (
                  <p key={i} className="cmp-bank-line bi-he">{l.he}</p>
                ))}
                {aside.bankLines.map((l, i) => (
                  l.ar ? <p key={`ar-${i}`} className="cmp-bank-line bi-ar">{l.ar}</p> : null
                ))}
              </div>
            </div>
          </div>

          <div className="cmp-actions">
            <button type="button" className="cmp-cta" onClick={() => { backToAmount(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
              <span className="bi-he">{d.donateNow.he}</span>
              <span className="bi-ar">{d.donateNow.ar}</span>
            </button>
            <a className="cmp-who" href="https://www.standing-together.org/about" target="_blank" rel="noopener noreferrer">
              <span className="bi-he">{d.whoWeAre.he}</span>
              <span className="bi-ar">{d.whoWeAre.ar}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
